/**
 * Etapas do kanban de ordens de serviço — modelo canônico.
 *
 * As colunas do kanban deixaram de ser fixas: a oficina cria, renomeia,
 * recolore, reordena e escolhe quais botões aparecem em cada uma. A
 * configuração inteira vive num único JSON, guardado na chave
 * `kanban_etapas` (SQLite local no desktop, coluna `kanban_etapas` de
 * `usuario_configuracoes` no servidor) — ou seja, viaja pelo mesmo trilho
 * dos termos de impressão e vale para as duas plataformas.
 *
 * Três etapas nunca somem, porque o resto do sistema depende delas:
 *
 * | Etapa        | Papel                                                    |
 * |--------------|----------------------------------------------------------|
 * | EmAberto     | onde toda OS nasce (`defaultValue` do banco)             |
 * | FechadoPago  | fim feliz — verde, alimenta faturamento/pós-venda        |
 * | Cancelado    | fim infeliz — vermelho, última coluna, só leitura        |
 *
 * `EmAberto` aceita rótulo, cor e botões próprios (só não pode ser
 * apagada nem sair da frente). `FechadoPago` e `Cancelado` são fixas de
 * verdade: cor travada, sem botões de ação, sempre no fim e sempre com a
 * pergunta de confirmação ligada.
 *
 * ⚠️ Este arquivo existe em três cópias idênticas — `src/` (UI),
 * `electron/services/` (desktop) e `server/src/app/` (web). Os alvos não
 * compartilham build, então mudou aqui, mude nos outros dois.
 */

/** Botões que podem aparecer no card de uma etapa. "Ver" é sempre exibido. */
export type AcaoEtapa = 'editar' | 'excluir' | 'contaReceber';

export const ACOES_ETAPA: AcaoEtapa[] = ['editar', 'excluir', 'contaReceber'];

export const ACAO_ROTULO: Record<AcaoEtapa, string> = {
  editar: 'Editar O.S.',
  excluir: 'Excluir O.S.',
  contaReceber: 'Criar conta a receber',
};

export const ACAO_DESCRICAO: Record<AcaoEtapa, string> = {
  editar: 'Abre a ordem de serviço para alteração de itens e valores.',
  excluir: 'Apaga a ordem de serviço. Use só em etapas iniciais.',
  contaReceber: 'Gera o lançamento no financeiro a partir da ordem de serviço.',
};

/** Pergunta feita antes de mover uma OS para a etapa. */
export type ConfirmacaoEtapa = {
  titulo: string;
  /** Aceita `{numero}` e `{etapa}` como marcadores. */
  mensagem: string;
  /** Linha de alerta em destaque. Vazia = não mostra. */
  aviso: string | null;
};

export type EtapaKanban = {
  id: string;
  rotulo: string;
  /** Cor em hex `#rrggbb`. Trava em verde/vermelho nas etapas finais. */
  cor: string;
  acoes: AcaoEtapa[];
  confirmacao: ConfirmacaoEtapa | null;
  /** Destacar "entrega atrasada" nos cards desta etapa. */
  alertaAtraso: boolean;
  /**
   * Botão "Aprovar" na página do link compartilhado (acompanhamento da
   * O.S.). Guarda o id da etapa para onde a ordem vai quando o cliente
   * aprova; `null` = a etapa não mostra botão nenhum.
   *
   * O destino nunca é uma etapa final: aprovar não fecha nem cancela a
   * O.S., só empurra o fluxo para a próxima coluna da oficina.
   */
  aprovacaoDestino: string | null;
};

export const ETAPA_INICIAL = 'EmAberto';
export const ETAPA_FECHADO = 'FechadoPago';
export const ETAPA_CANCELADO = 'Cancelado';

/** Etapas de fim de linha: entrou, não sai; o card fica só para consulta. */
export const ETAPAS_TERMINAIS: readonly string[] = [ETAPA_FECHADO, ETAPA_CANCELADO];

/** Etapas que o usuário não pode apagar (o sistema depende delas). */
export const ETAPAS_PROTEGIDAS: readonly string[] = [
  ETAPA_INICIAL,
  ETAPA_FECHADO,
  ETAPA_CANCELADO,
];

export const COR_FECHADO = '#16a34a';
export const COR_CANCELADO = '#dc2626';

/** Cor travada da etapa, ou null quando é livre. */
export function corTravada(id: string): string | null {
  if (id === ETAPA_FECHADO) return COR_FECHADO;
  if (id === ETAPA_CANCELADO) return COR_CANCELADO;
  return null;
}

export function ehTerminal(id: string): boolean {
  return ETAPAS_TERMINAIS.includes(id);
}

export function ehProtegida(id: string): boolean {
  return ETAPAS_PROTEGIDAS.includes(id);
}

/** Só o rótulo e a cor mudam nas etapas fixas — o resto é imposto. */
export function ehTotalmenteFixa(id: string): boolean {
  return ehTerminal(id);
}

export const LIMITE_ETAPAS = 12;
export const LIMITE_ROTULO = 48;
export const LIMITE_TEXTO_CONFIRMACAO = 240;

/**
 * As colunas que qualquer conta enxerga antes de personalizar nada —
 * exatamente o kanban que existia quando as etapas eram fixas no código,
 * mais a coluna "Cancelado" no fim.
 */
export const ETAPAS_PADRAO: readonly EtapaKanban[] = [
  {
    id: ETAPA_INICIAL,
    rotulo: 'Em aberto',
    cor: '#2563eb',
    acoes: ['editar', 'excluir'],
    confirmacao: null,
    alertaAtraso: true,
    aprovacaoDestino: null,
  },
  {
    id: 'AguardandoPeca',
    rotulo: 'Aguardando peça',
    cor: '#d97706',
    acoes: ['editar'],
    confirmacao: null,
    alertaAtraso: true,
    aprovacaoDestino: null,
  },
  {
    id: 'EmServico',
    rotulo: 'Em serviço',
    cor: '#7c3aed',
    acoes: ['editar'],
    confirmacao: null,
    alertaAtraso: true,
    aprovacaoDestino: null,
  },
  {
    id: 'ServicoConcluidoAguardandoPagamento',
    rotulo: 'Serviço concluído aguardando pagamento',
    cor: '#ea580c',
    acoes: ['editar', 'contaReceber'],
    confirmacao: null,
    alertaAtraso: false,
    aprovacaoDestino: null,
  },
  {
    id: ETAPA_FECHADO,
    rotulo: 'Fechado pago',
    cor: COR_FECHADO,
    acoes: [],
    confirmacao: {
      titulo: 'Fechar como pago?',
      mensagem: 'Deseja mudar a ordem de serviço {numero} para {etapa}?',
      aviso:
        'Depois de confirmado, esse status ficará bloqueado e não poderá ser alterado para outro.',
    },
    alertaAtraso: false,
    aprovacaoDestino: null,
  },
  {
    id: ETAPA_CANCELADO,
    rotulo: 'Cancelado',
    cor: COR_CANCELADO,
    acoes: [],
    confirmacao: {
      titulo: 'Cancelar ordem de serviço?',
      mensagem: 'Deseja mudar a ordem de serviço {numero} para {etapa}?',
      aviso:
        'Depois de cancelada, a ordem fica somente para consulta e não pode voltar para outro status.',
    },
    alertaAtraso: false,
    aprovacaoDestino: null,
  },
];

/** Confirmação usada quando a etapa fixa vem sem uma (JSON antigo/corrompido). */
function confirmacaoPadraoDe(id: string): ConfirmacaoEtapa | null {
  const padrao = ETAPAS_PADRAO.find((e) => e.id === id);
  return padrao?.confirmacao ? { ...padrao.confirmacao } : null;
}

const REGEX_COR = /^#[0-9a-fA-F]{6}$/;
const REGEX_ID = /^[A-Za-z][A-Za-z0-9_]{0,39}$/;

function texto(v: unknown, limite: number): string {
  if (typeof v !== 'string') return '';
  return v.replace(/\s+/g, ' ').trim().slice(0, limite);
}

function cor(v: unknown, padrao: string): string {
  if (typeof v !== 'string') return padrao;
  const t = v.trim();
  return REGEX_COR.test(t) ? t.toLowerCase() : padrao;
}

function acoes(v: unknown): AcaoEtapa[] {
  if (!Array.isArray(v)) return [];
  const out: AcaoEtapa[] = [];
  for (const item of v) {
    if (typeof item !== 'string') continue;
    const a = item as AcaoEtapa;
    if (ACOES_ETAPA.includes(a) && !out.includes(a)) out.push(a);
  }
  return out;
}

/**
 * Destino do botão de aprovação como veio do JSON. Aqui só o formato é
 * checado; se a etapa apontada existe (e pode receber a O.S.) é
 * `sanearAprovacoes` quem decide, porque isso depende da lista inteira.
 */
function aprovacaoDestino(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return REGEX_ID.test(t) ? t : null;
}

function confirmacao(v: unknown, id: string): ConfirmacaoEtapa | null {
  if (!v || typeof v !== 'object') {
    return ehTerminal(id) ? confirmacaoPadraoDe(id) : null;
  }
  const bruto = v as Record<string, unknown>;
  const titulo = texto(bruto.titulo, LIMITE_TEXTO_CONFIRMACAO);
  const mensagem = texto(bruto.mensagem, LIMITE_TEXTO_CONFIRMACAO);
  const aviso = texto(bruto.aviso, LIMITE_TEXTO_CONFIRMACAO);
  if (titulo === '' && mensagem === '') {
    return ehTerminal(id) ? confirmacaoPadraoDe(id) : null;
  }
  return {
    titulo: titulo || 'Confirmar mudança de etapa?',
    mensagem: mensagem || 'Deseja mudar a ordem de serviço {numero} para {etapa}?',
    aviso: aviso || null,
  };
}

/** Aplica no objeto tudo que a etapa é obrigada a ser (cor, ações, pergunta). */
function aplicarRegrasFixas(e: EtapaKanban): EtapaKanban {
  const travada = corTravada(e.id);
  if (!ehTerminal(e.id)) return travada ? { ...e, cor: travada } : e;
  return {
    ...e,
    cor: travada ?? e.cor,
    // Etapa final é ponto de chegada: nada de editar, excluir ou faturar
    // por ali. O card mostra só o botão de ver.
    acoes: [],
    confirmacao: e.confirmacao ?? confirmacaoPadraoDe(e.id),
    alertaAtraso: false,
    // A O.S. já chegou ao fim: não há o que o cliente aprovar.
    aprovacaoDestino: null,
  };
}

/**
 * Deixa só os destinos de aprovação que fazem sentido: a etapa apontada
 * precisa existir, não pode ser a própria e não pode ser final — o
 * cliente não fecha nem cancela a ordem pelo link, só aprova o passo
 * seguinte. Destino inválido (etapa apagada, JSON antigo) desliga o
 * botão em vez de deixá-lo apontando para o vazio.
 */
function sanearAprovacoes(etapas: EtapaKanban[]): EtapaKanban[] {
  const disponiveis = new Set(etapas.filter((e) => !ehTerminal(e.id)).map((e) => e.id));
  return etapas.map((e) => {
    if (e.aprovacaoDestino === null) return e;
    const valido = e.aprovacaoDestino !== e.id && disponiveis.has(e.aprovacaoDestino);
    return valido ? e : { ...e, aprovacaoDestino: null };
  });
}

/**
 * Recebe o que veio do banco (string JSON, objeto ou nada) e devolve uma
 * lista de etapas sempre utilizável: sem duplicatas, com as três etapas
 * obrigatórias presentes e na ordem certa (inicial na frente, fechado e
 * cancelado no fim, nesta ordem).
 */
export function normalizarEtapas(bruto: unknown): EtapaKanban[] {
  let dado: unknown = bruto;
  if (typeof dado === 'string') {
    const t = dado.trim();
    if (t === '') return ETAPAS_PADRAO.map((e) => ({ ...e, acoes: [...e.acoes] }));
    try {
      dado = JSON.parse(t);
    } catch {
      return ETAPAS_PADRAO.map((e) => ({ ...e, acoes: [...e.acoes] }));
    }
  }
  const lista = Array.isArray(dado)
    ? dado
    : Array.isArray((dado as { etapas?: unknown } | null)?.etapas)
      ? ((dado as { etapas: unknown[] }).etapas)
      : null;
  if (!lista) return ETAPAS_PADRAO.map((e) => ({ ...e, acoes: [...e.acoes] }));

  const vistos = new Set<string>();
  const etapas: EtapaKanban[] = [];
  for (const item of lista) {
    if (!item || typeof item !== 'object') continue;
    const b = item as Record<string, unknown>;
    const id = typeof b.id === 'string' ? b.id.trim() : '';
    if (!REGEX_ID.test(id) || vistos.has(id)) continue;
    vistos.add(id);
    const padrao = ETAPAS_PADRAO.find((e) => e.id === id);
    etapas.push(
      aplicarRegrasFixas({
        id,
        rotulo: texto(b.rotulo, LIMITE_ROTULO) || padrao?.rotulo || id,
        cor: cor(b.cor, padrao?.cor ?? '#64748b'),
        acoes: acoes(b.acoes),
        confirmacao: confirmacao(b.confirmacao, id),
        alertaAtraso:
          typeof b.alertaAtraso === 'boolean' ? b.alertaAtraso : (padrao?.alertaAtraso ?? true),
        // Sem herdar do padrão: JSON gravado antes deste recurso não pode
        // ligar sozinho um botão que o cliente final enxerga.
        aprovacaoDestino: aprovacaoDestino(b.aprovacaoDestino),
      }),
    );
    if (etapas.length >= LIMITE_ETAPAS) break;
  }

  // As obrigatórias entram de volta com o padrão se o JSON não as trouxe.
  for (const id of ETAPAS_PROTEGIDAS) {
    if (vistos.has(id)) continue;
    const padrao = ETAPAS_PADRAO.find((e) => e.id === id)!;
    etapas.push(aplicarRegrasFixas({ ...padrao, acoes: [...padrao.acoes] }));
  }

  return sanearAprovacoes(ordenarEtapas(etapas));
}

/** Inicial na frente; fechado e cancelado no fim (cancelado por último). */
export function ordenarEtapas(etapas: EtapaKanban[]): EtapaKanban[] {
  const peso = (id: string) =>
    id === ETAPA_INICIAL ? 0 : id === ETAPA_CANCELADO ? 3 : id === ETAPA_FECHADO ? 2 : 1;
  return etapas
    .map((e, i) => ({ e, i }))
    .sort((a, b) => peso(a.e.id) - peso(b.e.id) || a.i - b.i)
    .map(({ e }) => e);
}

export function serializarEtapas(etapas: EtapaKanban[]): string {
  return JSON.stringify({ versao: 1, etapas: normalizarEtapas(etapas) });
}

/** Gera um id livre a partir do rótulo digitado ("Lavagem" → "Lavagem_7f3a"). */
export function novoIdEtapa(rotulo: string, existentes: readonly string[]): string {
  const base =
    rotulo
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Za-z0-9]/g, '')
      .slice(0, 24) || 'Etapa';
  const inicio = /^[A-Za-z]/.test(base) ? base : `E${base}`;
  let id = inicio;
  let n = 2;
  while (existentes.includes(id)) {
    id = `${inicio}_${n}`;
    n += 1;
  }
  return id;
}

/** Rótulo da etapa; devolve o próprio id quando a etapa não existe mais. */
export function rotuloEtapa(etapas: readonly EtapaKanban[], id: string): string {
  return etapas.find((e) => e.id === id)?.rotulo ?? id;
}

export function corEtapa(etapas: readonly EtapaKanban[], id: string): string {
  return etapas.find((e) => e.id === id)?.cor ?? '#64748b';
}

/**
 * Etapa para onde o botão "Aprovar" do link compartilhado leva a O.S.
 * que está em `status` — ou `null` quando aquela etapa não tem botão.
 * É a mesma resposta no desktop, no web e na página pública, porque a
 * configuração é uma só.
 */
export function destinoAprovacao(
  etapas: readonly EtapaKanban[],
  status: string,
): EtapaKanban | null {
  const atual = etapas.find((e) => e.id === status);
  if (!atual?.aprovacaoDestino) return null;
  return etapas.find((e) => e.id === atual.aprovacaoDestino) ?? null;
}

/** Etapas que podem ser destino de uma aprovação (exclui finais e a própria). */
export function destinosDeAprovacao(
  etapas: readonly EtapaKanban[],
  idEtapa: string,
): EtapaKanban[] {
  return etapas.filter((e) => !ehTerminal(e.id) && e.id !== idEtapa);
}

/** Preenche `{numero}` e `{etapa}` no texto da pergunta de confirmação. */
export function textoConfirmacao(
  modelo: string,
  dados: { numero: string; etapa: string },
): string {
  return modelo.replace(/\{numero\}/g, dados.numero).replace(/\{etapa\}/g, dados.etapa);
}
